import { useState, useCallback } from "react";
import { fetchRefContent, refreshRef } from "../api";

export default function ReferencesPanel({ references }) {
  const [expanded, setExpanded] = useState(null); // hash of expanded ref
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [refreshingHash, setRefreshingHash] = useState(null);
  const [refStatuses, setRefStatuses] = useState({}); // hash -> status override

  const handleToggle = useCallback(
    (ref) => {
      if (expanded === ref.hash) {
        setExpanded(null);
        setContent(null);
        return;
      }
      setExpanded(ref.hash);
      setLoading(true);
      setContent(null);
      fetchRefContent(ref.hash)
        .then((data) => setContent(data.content))
        .catch(() => setContent("[Failed to load content]"))
        .finally(() => setLoading(false));
    },
    [expanded]
  );

  const handleRefresh = useCallback((ref) => {
    setRefreshingHash(ref.hash);
    refreshRef(ref.hash, ref.url)
      .then(() => {
        setRefStatuses((prev) => ({ ...prev, [ref.hash]: "cached" }));
        // If this ref is expanded, reload content
        if (expanded === ref.hash) {
          setLoading(true);
          fetchRefContent(ref.hash)
            .then((data) => setContent(data.content))
            .catch(() => setContent("[Failed to load content]"))
            .finally(() => setLoading(false));
        }
      })
      .catch(() => {})
      .finally(() => setRefreshingHash(null));
  }, [expanded]);

  const refs = references || [];
  const getStatus = (ref) => refStatuses[ref.hash] || ref.status;
  const cached = refs.filter((r) => getStatus(r) === "cached").length;

  return (
    <div className="refs-panel">
      <div className="refs-header">
        References
        <span className="refs-count">
          {refs.length > 0 ? `${cached}/${refs.length} cached` : "none"}
        </span>
      </div>
      <div className="refs-list">
        {refs.map((ref) => {
          const status = getStatus(ref);
          return (
            <div key={ref.hash} className="ref-entry">
              <div className="ref-item">
                <span
                  className={`ref-status ref-status-${status}`}
                  title={status}
                />
                <a
                  href={ref.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="ref-url"
                >
                  {ref.url}
                </a>
                {ref.tags && ref.tags.length > 0 && ref.tags.map((tag) => (
                  <span key={`nvd-${tag}`} className="ref-tag ref-tag-nvd" title="NVD/CNA tag">{tag}</span>
                ))}
                {ref.llm_tags && ref.llm_tags.length > 0 && ref.llm_tags
                  .filter((tag) => !(ref.tags || []).includes(tag))
                  .map((tag) => (
                  <span key={`llm-${tag}`} className="ref-tag ref-tag-llm-only" title="LLM tag (not in NVD)">{tag}</span>
                ))}
                {status === "cached" ? (
                  <button
                    className={`ref-cached-pill ${expanded === ref.hash ? "ref-cached-pill-active" : ""}`}
                    onClick={() => handleToggle(ref)}
                    title={expanded === ref.hash ? "Hide cached content" : "View cached content"}
                  >
                    {expanded === ref.hash ? "hide" : "cached"}
                  </button>
                ) : (
                  <span className="ref-unavailable-pill">unavailable</span>
                )}
                <button
                  className="ref-refresh-btn"
                  onClick={() => handleRefresh(ref)}
                  disabled={refreshingHash === ref.hash}
                  title="Re-fetch reference content"
                >
                  {refreshingHash === ref.hash ? "..." : "\u21bb"}
                </button>
              </div>
              {expanded === ref.hash && (
                <div className="ref-content">
                  {loading ? (
                    <div className="ref-content-loading">Loading...</div>
                  ) : (
                    <pre className="ref-content-text">{content}</pre>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
